import * as React from 'react';
import type { SuperDoc } from 'superdoc';
import { readDocxArrayBufferFromBlob } from '@/lib/docxBuffer';

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function docxFileName(title: string) {
  const cleanedTitle = title.trim().replace(/[^\w .-]+/g, '-').replace(/\s+/g, '-');
  return `${cleanedTitle || 'document'}.docx`;
}

export function arrayBufferToDocxFile(buffer: ArrayBuffer, title: string) {
  return new File([buffer], docxFileName(title), { type: DOCX_MIME_TYPE });
}

export async function exportSuperDocDocxBuffer(superdoc: SuperDoc): Promise<ArrayBuffer> {
  let directExportError: unknown = null;

  try {
    const docxBlobs = await superdoc.exportEditorsToDOCX({
      commentsType: 'external',
      isFinalDoc: false,
      fieldsHighlightColor: null,
    });
    for (const blob of docxBlobs) {
      try {
        return await readDocxArrayBufferFromBlob(blob);
      } catch (error) {
        directExportError = error;
      }
    }
  } catch (error) {
    directExportError = error;
  }

  const fallbackBlob = await superdoc.export({
    commentsType: 'external',
    exportType: ['docx'],
    triggerDownload: false,
  });
  try {
    return await readDocxArrayBufferFromBlob(fallbackBlob);
  } catch (error) {
    if (directExportError) {
      console.info('Direct SuperDoc DOCX export was unavailable:', directExportError);
    }
    throw error;
  }
}

export function superDocErrorMessage(eventOrError: unknown): string {
  if (eventOrError instanceof Error) return eventOrError.message;
  if (eventOrError && typeof eventOrError === 'object') {
    const maybeError = (eventOrError as { error?: unknown }).error;
    if (maybeError instanceof Error) return maybeError.message;
    if (typeof maybeError === 'string') return maybeError;
  }
  if (typeof eventOrError === 'string') return eventOrError;
  return 'SuperDoc could not load this DOCX.';
}

/**
 * SuperDoc's collab table projector looks up `tbl:{cell paraId}` in the last
 * projected story XML. Select-all delete drops that span; undo restores the
 * table in Yjs and the next refresh throws "span drift". The live room still
 * has the undone document, so rejoining is safe; treating this as fatal
 * unmounts the editor and can checkpoint the emptied export fallback.
 */
export function isRecoverableSuperDocTableProjectionError(message: string): boolean {
  return message.includes('table structural projection span drift');
}

export function usePortalElementRect(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  portalContainer: HTMLElement | null,
): React.CSSProperties | null {
  const [rect, setRect] = React.useState<React.CSSProperties | null>(null);

  React.useLayoutEffect(() => {
    if (!enabled || !portalContainer) {
      setRect(null);
      return undefined;
    }

    const element = ref.current;
    if (!element) return undefined;

    const updateRect = () => {
      const nextRect = element.getBoundingClientRect();
      if (portalContainer === document.body) {
        setRect({
          height: nextRect.height,
          left: nextRect.left,
          position: 'fixed',
          top: nextRect.top,
          width: nextRect.width,
          zIndex: 20,
        });
        return;
      }

      const containerRect = portalContainer.getBoundingClientRect();
      setRect({
        height: nextRect.height,
        left: nextRect.left - containerRect.left + portalContainer.scrollLeft,
        position: 'absolute',
        top: nextRect.top - containerRect.top + portalContainer.scrollTop,
        width: nextRect.width,
        zIndex: 20,
      });
    };

    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(element);
    resizeObserver.observe(portalContainer);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [enabled, portalContainer, ref]);

  return rect;
}

export function getEditorScrollContainer(frame: HTMLElement | null) {
  return frame?.querySelector<HTMLElement>('.superdoc__sub-document') ?? null;
}

export function centerEditorScroll(frame: HTMLElement | null) {
  const scrollContainer = getEditorScrollContainer(frame);
  if (!scrollContainer) return;

  scrollContainer.scrollLeft = Math.max(0, (scrollContainer.scrollWidth - scrollContainer.clientWidth) / 2);
}
